import React, { useState } from 'react';
import { portfolioData } from '../data/mock';
import { Link, ExternalLink, Calendar, Code } from 'lucide-react';
import useAnalytics from '../hooks/useAnalytics';
import DecryptedText from './reactbits/DecryptedText';
import BlurText from './reactbits/BlurText';
import SpotlightCard from './reactbits/SpotlightCard';
import './PortfolioVersions.css';

const PortfolioVersions = () => {
  const { trackEvent } = useAnalytics();
  const [hoveredCard, setHoveredCard] = useState(null);
  const { portfolioVersions } = portfolioData;

  const getStatusColor = (status, badge) => {
    const text = (status || badge || '').toLowerCase();
    if (text.includes('live') || text.includes('v2.0')) return '#00FFD1';
    if (text.includes('development') || text.includes('v3.0') || text.includes('coming')) return '#00A3FF';
    return '#888888';
  };

  const DEFAULT_FALLBACK_IMAGE = "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=800&q=80";

  return (
    <section className="versions-section" id="versions">
      <div className="versions-bg-pattern"></div>

      <div className="versions-container">
        {/* Header */}
        <div className="section-header">
          <div className="header-line"></div>
          <h2 className="section-title">
            <span className="title-bracket">{'['}</span>
            <DecryptedText text="Portfolio Timeline" speed={40} maxIterations={12} animateOn="view" />
            <span className="title-bracket">{']'}</span>
          </h2>
          <div className="section-subtitle">
            <BlurText text="Explore the evolution of my portfolio—from the first version to the latest, showcasing how my design, development, and skills have grown over time." delay={60} animateBy="words" />
          </div>
        </div>

        {/* Versions Grid */}
        <div className="versions-grid">
          {portfolioVersions.map((item, index) => {
            const statusColor = getStatusColor(item.status, item.badge);
            return (
              <div
                key={item.id}
                className={`project-card version-card ${hoveredCard === item.id ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredCard(item.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ '--delay': `${index * 100}ms` }}
              >
                {/* Card Glow Effect */}
                <div className="card-glow"></div>

                {/* Status Badge - Top Right */}
                <div className="project-status" style={{ '--status-color': statusColor }}>
                  <span className="version-status-dot" style={{ backgroundColor: statusColor }}></span>
                  <span>{item.status || item.badge}</span>
                </div>

                {/* Cover Image Container */}
                <div className="project-image-container">
                  {/* Version Tag - Top Left */}
                  {item.badge && (
                    <div className="version-badge-top-left" style={{ borderColor: statusColor, color: statusColor }}>
                      <span>{item.badge}</span>
                    </div>
                  )}

                  <img
                    src={item.coverImage}
                    alt={item.title}
                    className="project-image"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = DEFAULT_FALLBACK_IMAGE;
                    }}
                  />
                  <div className="image-overlay">
                    <div className="overlay-grid"></div>
                    {/* Subtitle overlaid on bottom of cover image */}
                    {item.subtitle && (
                      <div className="version-subtitle-overlay">
                        <Calendar className="subtitle-icon" />
                        <span>{item.subtitle}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Content */}
                <div className="project-content">
                  <h3 className="project-title" style={{ marginTop: '0.5rem' }}>{item.title}</h3>
                  <p className="project-description">{item.description}</p>

                  {/* Actions */}
                  <div className="project-actions">
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="action-btn primary"
                      onClick={() => trackEvent('version_archive_click', { version: item.title, url: item.url })}
                    >
                      {item.status === 'Development' ? <Code className="btn-icon" /> : <ExternalLink className="btn-icon" />}
                      <span>{item.status === 'Development' ? 'In Development' : (item.status === 'Live' ? 'Launch Node' : 'Access Archive')}</span>
                      <div className="btn-glow"></div>
                    </a>
                  </div>
                </div>

                {/* Cyber Corner Markers */}
                <div className="mechanical-corners">
                  <div className="corner top-left"></div>
                  <div className="corner top-right"></div>
                  <div className="corner bottom-left"></div>
                  <div className="corner bottom-right"></div>
                </div>

                {/* Holographic Data Stream Line */}
                <div className="data-stream">
                  <div className="stream-line"></div>
                  <div className="stream-dots">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PortfolioVersions;
